import { BASE_DOORKNOCK } from "@/lib/constants/doorknockEventConsts";
import { APIResponse, PaginatedResponse } from "@simplerealtybits/srb-types";
import { DoorKnock } from "@t/DoorKnock";
import { DoorKnockEvent } from "@t/DoorKnockEvent";
import { GetTargetsResponseDto } from "@t/GetTargetsResponseDto";
import { QueryClient } from "@tanstack/react-query";

export type QueryKey<T> = [
    T & {
        _id: string;
        baseURL: string;
        path?: string;
        query?: Record<string, unknown>;
        body?: Record<string, unknown>;
        headers?: Record<string, string>;
        _infinite?: boolean;
    },
];

export const createQueryKey = <T>(
    id: string,
    options?: T,
    infinite?: boolean
): [QueryKey<T>[0]] => {
    const params: QueryKey<T>[0] = {
        _id: id,
    } as QueryKey<T>[0];

    if (options) {
        params.body = options;
    }

    if (infinite) {
        params._infinite = infinite;
    }

    return [params];
};

/**
 * Function to clear the target query cache and update the target interactions
 * with the new door knock event data.
 * @param queryClient - The QueryClient instance to use for cache manipulation.
 * @param data - The DoorKnockEventDto data to be used for updating the target interactions.
 */
export function clearTargetQuery(queryClient: QueryClient, data?: DoorKnockEvent) {
    queryClient.invalidateQueries({
        predicate: (query) => {
            const queryKey = query.queryKey[0] as QueryKey<DoorKnockEvent>[0];
            return queryKey._id == createQueryKey("doorknockEvents")[0]._id;
        },
    });

    queryClient
        .getQueryCache()
        .findAll({
            predicate: (query) => {
                const queryKey = query.queryKey[0] as QueryKey<DoorKnockEvent>[0];
                return queryKey._id == createQueryKey("fetchTargets")[0]._id;
            },
        })
        .forEach((query) => {
            queryClient.setQueryData(query.queryKey, (prev: APIResponse<GetTargetsResponseDto>) => {
                if (!prev || !data) return prev;

                const newData = { ...prev.data };

                if (!newData || newData.knocks == null) return prev;

                const targetIdx = newData.knocks.findIndex((t) => t.oid == data.oid);

                if (targetIdx === -1) {
                    const newKnock: DoorKnock = {
                        ...BASE_DOORKNOCK,
                        oid: data.oid,
                        fullAddress: data.fullAddress,
                        addressLine1: data.addressLine1,
                        addressLine2: data.addressLine2,
                        city: data.city,
                        state: data.state,
                        zip: data.zip,
                        country: data.country,
                        latitude: data.latitude,
                        longitude: data.longitude,
                        events: [data],
                    };

                    newData.knocks.push(newKnock);
                } else {
                    const interactions = newData.knocks[targetIdx].events;
                    newData.knocks[targetIdx].events = [data, ...interactions];
                }

                return { ...prev, data: newData };
            });
        });

    queryClient
        .getQueryCache()
        .findAll({
            predicate: (query) => {
                const queryKey = query.queryKey[0] as QueryKey<DoorKnockEvent>[0];
                return queryKey._id == createQueryKey("searchTargets")[0]._id;
            },
        })
        .forEach((query) => {
            queryClient.setQueryData(query.queryKey, (prev: PaginatedResponse<DoorKnock>) => {
                if (!prev || !data) return prev;

                const newData = [...prev.data];
                console.log("Updating door knock events in target query", newData, prev);

                if (!newData || newData == null) return prev;

                const targetIdx = newData.findIndex((t) => t.id == data.id);
                console.log("Target index found:", targetIdx);

                if (targetIdx === -1) {
                    const newKnock: DoorKnock = {
                        ...BASE_DOORKNOCK,
                        id: data.doorKnockId || 0,
                        oid: data.oid,
                        fullAddress: data.fullAddress,
                        addressLine1: data.addressLine1,
                        addressLine2: data.addressLine2,
                        city: data.city,
                        state: data.state,
                        zip: data.zip,
                        country: data.country,
                        latitude: data.latitude,
                        longitude: data.longitude,
                        events: [data],
                    };

                    newData.push(newKnock);
                } else {
                    const interactions = newData[targetIdx].events;
                    newData[targetIdx].events = [data, ...interactions];
                }
                const updatedQueryData = { ...prev, data: newData };
                console.log("Updated target query data:", updatedQueryData);

                return updatedQueryData;
            });
        });
}

export function clearQuery(queryClient: QueryClient, queryId: string) {
    queryClient.invalidateQueries({
        predicate: (query) => {
            const queryKey = query.queryKey[0] as QueryKey<unknown>[0];
            return queryKey._id == createQueryKey(queryId)[0]._id;
        },
    });
}

export async function parseResponse<
    T,
    P = false,
    R extends { success: boolean; message: string } = P extends true
        ? PaginatedResponse<T>
        : APIResponse<T>,
>(response: Response, errorMessage: string = ""): Promise<R> {
    if (!response.ok) throw new Error(errorMessage);

    const data: R = await response.json();

    if (data.success == false) throw new Error(data.message || errorMessage);

    return data;
}

export async function parsePaginatedResponse<T>(
    response: Response,
    errorMessage: string = ""
): Promise<PaginatedResponse<T>> {
    if (!response.ok) throw new Error(errorMessage);

    const data = (await response.json()) as PaginatedResponse<T>;

    if (data.success == false) throw new Error(data.message || errorMessage);

    return data;
}
